import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiMail, FiMapPin, FiGlobe } from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialForm = {
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const submitTimer = useRef(null);

  useEffect(() => {
    document.title = `Contact | ${siteConfig.displayName}`;
    return () => window.clearTimeout(submitTimer.current);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    ["firstName", "lastName", "email", "subject", "message"].forEach((field) => {
      if (!formData[field].trim()) nextErrors[field] = "This field is required.";
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (formData.message && formData.message.trim().length < 10) {
      nextErrors.message = "Tell us a little more so we can help.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setIsSubmitting(true);
    submitTimer.current = window.setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setFormData(initialForm);
    }, 700);
  };

  const input = (name, label, type = "text", optional = false) => (
    <label className={errors[name] ? "has-error" : ""} htmlFor={`contact-${name}`}>
      <span>{label}{optional ? " (optional)" : ""}</span>
      <input
        id={`contact-${name}`}
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={label}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `contact-${name}-error` : undefined}
      />
      {errors[name] && <small id={`contact-${name}-error`} role="alert">{errors[name]}</small>}
    </label>
  );

  return (
    <main className="contact-page">
      <nav className="care-tabs" aria-label="Customer care">
        <Link className="is-active" to="/contact">Contact</Link>
        <Link to="/shipping">Shipping</Link>
        <Link to="/returns">Returns</Link>
        <Link to="/shipping#payment-options">Payment options</Link>
      </nav>

      <header className="contact-heading">
        <p className="eyebrow">WE'RE HERE TO HELP</p>
        <h1>Online customer care</h1>
        <p>
          Questions about a product, fit or an existing order? Send us a note and
          our team will get back to you within two business days.
        </p>
      </header>

      <section className="contact-channels">
        <article><FiMail /><span>Email</span><a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a></article>
        <article><FiGlobe /><span>Website</span><a href={siteConfig.website}>{siteConfig.website.replace("https://", "")}</a></article>
        <article><FiMapPin /><span>Visit</span><p>{siteConfig.contact.address}</p></article>
      </section>

      <section className="contact-form-section">
        <header>
          <h2>Other inquiries</h2>
          <p>Please fill in the fields below. Required fields are marked by an asterisk.</p>
        </header>
        {showSuccess && (
          <div className="contact-success" role="status">
            <FiCheck />
            <span><strong>Message received.</strong> We'll be in touch shortly.</span>
            <button onClick={() => setShowSuccess(false)}>Close</button>
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <div className="contact-form__row">
            {input("firstName", "First name *")}
            {input("lastName", "Last name *")}
          </div>
          {input("company", "Company", "text", true)}
          {input("email", "Email *", "email")}
          {input("phone", "Telephone", "tel", true)}
          <label className={errors.subject ? "has-error" : ""} htmlFor="contact-subject">
            <span>Topic *</span>
            <select id="contact-subject" name="subject" value={formData.subject} onChange={handleChange} aria-invalid={Boolean(errors.subject)} aria-describedby={errors.subject ? "contact-subject-error" : undefined}>
              <option value="">Select a topic</option>
              <option value="order">My order</option>
              <option value="product">Product and sizing</option>
              <option value="return">A return</option>
              <option value="press">Press or partnership</option>
              <option value="other">Other</option>
            </select>
            {errors.subject && <small id="contact-subject-error" role="alert">{errors.subject}</small>}
          </label>
          <label className={errors.message ? "has-error" : ""} htmlFor="contact-message">
            <span>Message *</span>
            <textarea
              id="contact-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="How can we help?"
              rows="6"
              aria-invalid={Boolean(errors.message)}
              aria-describedby={errors.message ? "contact-message-error" : undefined}
            />
            {errors.message && <small id="contact-message-error" role="alert">{errors.message}</small>}
          </label>
          <button className="button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending…" : "Submit"}
          </button>
        </form>
      </section>

      <section className="contact-store">
        <img src="/img/generated/product-28-view-05.jpg" alt="Vappyfurhome customer care editorial" loading="lazy" />
        <div><h2>{siteConfig.companyName}</h2><p>{siteConfig.contact.address}</p><span>{siteConfig.contact.email} · {siteConfig.contact.hours}</span></div>
      </section>
    </main>
  );
}

export default Contact;
